import { PayloadAction } from '@reduxjs/toolkit';
import { NormalizeOAS, OASOutput, OASRequestParams } from 'fets';
import { call, put, select, takeLatest } from 'redux-saga/effects';

import { authAdd, restCall } from '@/core/clients/rest';
import type oas from '@/services/rest/base/openapi';

import { appSettingsActions } from '../app/slice';
import { systemModalsActions } from '../modals/smokeExtraction/system/slice';
import { ISystemPerfomCalculationPayload } from '../smokeExtraction/system/calculations/slice';
import { systemSelectors } from '../smokeExtraction/system/selectors';
import {
  ISystemUpdatePayload,
  systemActions,
} from '../smokeExtraction/system/slice';

import {
  airExchangeTableActions,
  IAirExchangeTableList,
  IAirExchangeTableListFetchPayload,
} from './slice';

type AirExchangeTableListResponse = OASOutput<
  NormalizeOAS<typeof oas>,
  '/air_exchange/calcs',
  'get',
  '200'
>;

type AirExchangeTable = OASRequestParams<
  NormalizeOAS<typeof oas>,
  '/air_exchange/air_exchange_table',
  'post'
>;

type SystemUpdateRequest = OASRequestParams<
  NormalizeOAS<typeof oas>,
  '/smoke_extraction/smoke_extraction_calc/{id}',
  'put'
>;

type ExplicationRequest = OASRequestParams<
  NormalizeOAS<typeof oas>,
  '/air_exchange/air_exchange_table/{id}/import',
  'post'
>;

type ExportJSONRequest = OASRequestParams<
  NormalizeOAS<typeof oas>,
  '/air_exchange/smoke_extraction_calc/{id}/export_json',
  'get'
>;

type ImportJSONRequest = OASRequestParams<
  NormalizeOAS<typeof oas>,
  '/air_exchange/air_exchange_table/{id}/import_json',
  'post'
>;

function* fetchAirExchangeTableList(
  action: PayloadAction<IAirExchangeTableListFetchPayload>
): Generator<any, void, AirExchangeTableListResponse> {
  const { order_by, page, size, search } = action.payload;

  try {
    const response = yield call(restCall, '/air_exchange/calcs', 'get', {
      query: { order_by, page, size, search },
      ...authAdd(),
    });

    const airExchangeList: IAirExchangeTableList = response;

    yield put(airExchangeTableActions.setAirExchangeTableList(airExchangeList));
  } catch (error) {
    console.log('Error on air exchange table list fetching');
  }
}

function* performAirExchangeTable(
  action: PayloadAction<ISystemPerfomCalculationPayload>
): Generator<any, void, any> {
  const { calc_id, data } = action.payload;

  const system = yield select(systemSelectors.system);

  const request: AirExchangeTable = {
    json: {
      calc_id: calc_id,
      state: data,
    },
    ...authAdd(),
  };

  try {
    const response = yield call(
      restCall,
      '/air_exchange/air_exchange_table',
      'post',
      request
    );

    if (!response.state) {
      throw new Error();
    }

    yield put(systemActions.setSystem({ ...system, state: response.state }));

    yield put(
      airExchangeTableActions.setAddRoomModal({
        isRender: false,
      })
    );

    yield put(
      appSettingsActions.setAppMessage({
        type: 'success',
        content: 'Данные успешно обновлены',
      })
    );
  } catch (error) {
    console.log('Error on performAirExchangeTable', error);
    yield put(
      appSettingsActions.setAppMessage({
        type: 'warning',
        content: 'Что-то пошло не так',
      })
    );
  } finally {
    yield put(airExchangeTableActions.setIsSending(false));
  }
}

function* updateNameSystem(
  action: PayloadAction<ISystemUpdatePayload>
): Generator<any, void, any> {
  const { systemID, name } = action.payload;

  const request: SystemUpdateRequest = {
    params: {
      id: systemID,
    },
    json: {
      name: name,
    },
    ...authAdd(),
  };

  try {
    yield call(
      restCall,
      '/smoke_extraction/smoke_extraction_calc/{id}',
      'put',
      request
    );
    yield put(
      systemActions.fetchSystem({
        id: systemID,
      })
    );
  } catch (error: any) {
    console.log('Error on update system', error);
    yield put(
      appSettingsActions.setAppMessage({
        type: 'warning',
        content: 'Что-то пошло не так',
      })
    );
  }
}

function* importOfExplication(
  action: PayloadAction<any | undefined | null>
): Generator<any, void, any> {
  const { id, formData } = action.payload;

  const request: ExplicationRequest = {
    params: {
      id,
    },
    formData,
    ...authAdd(),
  };

  try {
    const response = yield call(
      restCall,
      '/air_exchange/air_exchange_table/{id}/import',
      'post',
      request
    );

    yield put(
      airExchangeTableActions.setExplicationModal({
        isRender: false,
      })
    );

    yield put(
      systemActions.fetchSystem({
        id: id,
      })
    );
    yield put(
      appSettingsActions.setAppMessage({
        type: 'success',
        content: 'Данные успешно загружены',
      })
    );
  } catch (error) {
    console.log('Error on importOfExplication');
    yield put(
      airExchangeTableActions.setExplicationModal({
        isRender: false,
      })
    );
    yield put(
      appSettingsActions.setAppMessage({
        type: 'warning',
        content: 'Что-то пошло не так',
      })
    );
  }
}

function* importJSON(
  action: PayloadAction<any | undefined | null>
): Generator<any, void, any> {
  const { id, formData } = action.payload;

  const request: ImportJSONRequest = {
    params: {
      id,
    },
    formData,
    ...authAdd(),
  };

  try {
    yield call(
      restCall,
      '/air_exchange/air_exchange_table/{id}/import_json',
      'post',
      request
    );

    yield put(systemModalsActions.closeImportFromBIMModal());

    yield put(
      systemActions.fetchSystem({
        id: id,
      })
    );
    yield put(
      appSettingsActions.setAppMessage({
        type: 'success',
        content: 'Данные успешно загружены',
      })
    );
  } catch (error) {
    console.log('Error on importJSON');
    yield put(systemModalsActions.closeImportFromBIMModal());
    yield put(
      appSettingsActions.setAppMessage({
        type: 'warning',
        content: 'Что-то пошло не так',
      })
    );
  }
}

function* exportJSON(action: PayloadAction<any>): Generator<any, void, any> {
  const { id, name } = action.payload;

  const request: ExportJSONRequest = {
    params: {
      id: id,
    },
    ...authAdd(),
  };

  try {
    const jsonData = yield call(
      restCall,
      '/air_exchange/smoke_extraction_calc/{id}/export_json',
      'get',
      request
    );

    const dataStr = JSON.stringify(jsonData);

    const a = document.createElement('a');
    const file = new Blob([dataStr], { type: 'application/json' });
    a.href = URL.createObjectURL(file);
    a.download = `${name}__export.json`;

    document.body.appendChild(a);
    a.click();
    setTimeout(() => {
      document.body.removeChild(a);
    }, 100);
  } catch (error: any) {
    yield put(
      appSettingsActions.setAppMessage({
        type: 'warning',
        content: 'Что-то пошло не так',
      })
    );
  }
}

export const airExchangeTableSagas = [
  takeLatest(
    airExchangeTableActions.fetchAirExchangeTableList,
    fetchAirExchangeTableList
  ),
  takeLatest(
    airExchangeTableActions.performAirExchangeTable,
    performAirExchangeTable
  ),

  takeLatest(airExchangeTableActions.updateNameSystem, updateNameSystem),
  takeLatest(airExchangeTableActions.importOfExplication, importOfExplication),
  takeLatest(airExchangeTableActions.exportJSON, exportJSON),
  takeLatest(airExchangeTableActions.importJSON, importJSON),
];
